import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Chip,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  AccountCircle,
  Business,
  Person,
  AdminPanelSettings,
  Message as MessageIcon,
  ShoppingCart,
  Menu as MenuIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  PersonAdd as PersonAddIcon,
  Instagram,
  Facebook,
  Storefront,
  Article,
  Forum,
  Event as EventIcon,
  Login,
  ManageAccounts,
  Dashboard,
  PhotoLibrary,
  Settings,
  Logout
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBadge from '../Notifications/NotificationBadge';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import MessageBadge from '../Messages/MessageBadge';
import { useLayout } from '../../contexts/LayoutContext';
import { useTheme } from '@mui/material/styles';
import { useCart } from '../../contexts/CartContext';
import { PAYMENT_ENABLED } from '../../config/features';
import useUnreadNotifications from '../../hooks/useUnreadNotifications'; // ÚJ: Hook importálása
import useUnreadMessages from '../../hooks/useUnreadMessages';

const Header = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isProvider, isAdmin, logout } = useAuth();
  const { isHeaderTransparent, headerRef } = useLayout();
  const theme = useTheme();
  const { cartItems } = useCart();
  const { unreadCount: notificationCount } = useUnreadNotifications();
  const { unreadCount: messageCount } = useUnreadMessages();
  const totalUnreadCount = (notificationCount || 0) + (messageCount || 0);

  const appBarElevation = isHeaderTransparent ? 0 : 4;

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    setMobileOpen(false);
    navigate('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return `${user.firstName} ${user.lastName}`;
  };

  const getUserTypeIcon = () => {
    if (isAdmin()) return <AdminPanelSettings />;
    if (isProvider()) return <Business />;
    return <Person />;
  };

  const getUserTypeLabel = () => {
    if (isAdmin()) return 'Admin';
    if (isProvider()) return 'Szolgáltató';
    return 'Ügyfél';
  };

  const getUserTypeColor = () => {
    if (isAdmin()) return 'error';
    if (isProvider()) return 'secondary';
    return 'primary';
  };

  const navItems = [
    { name: 'Szolgáltatók', path: '/szolgaltatok', icon: <Storefront /> },
    { name: 'Blog', path: '/blog', icon: <Article /> },
    { name: 'Fórum', path: '/forum', icon: <Forum /> },
  ];

  const getNavItemsForUser = () => {
    const items = [...navItems];
    if (isAuthenticated && user?.userType === 'CLIENT') {
      items.push({ name: 'Esküvő Tervező', path: '/wedding-planner', icon: <EventIcon /> });
    }
    return items;
  };

  // Mobil menü tartalma
  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        Wedigo
      </Typography>
      <Divider />
      <List sx={{ '& .MuiListItemIcon-root': { minWidth: 40 } }}>
        {getNavItemsForUser().map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton 
              component={Link} 
              to={item.path} 
              sx={{ textAlign: 'left' }}
              onClick={handleDrawerToggle}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
        <Divider />
        {!isAuthenticated ? (
          <>
            <ListItem disablePadding>
              <ListItemButton 
                component={Link} 
                to="/login" 
                sx={{ textAlign: 'left' }}
                onClick={handleDrawerToggle}
              >
                <ListItemIcon><Login /></ListItemIcon>
                <ListItemText primary="Bejelentkezés" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ px: 1, py: 0.5 }}>
              <ListItemButton
                component={Link}
                to="/register"
                onClick={handleDrawerToggle}
                sx={{
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  color: 'primary.main',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '& .MuiListItemText-primary': { color: 'white' },
                    '& .register-icon': { color: 'white' },
                  },
                }}
              >
                <ListItemIcon>
                  <PersonAddIcon className="register-icon" fontSize="small" sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Regisztráció"
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <>
            <ListItem disablePadding>
              <ListItemButton 
                component={Link} 
                to="/dashboard" 
                sx={{ textAlign: 'left' }}
                onClick={handleDrawerToggle}
              >
                <ListItemIcon><Dashboard /></ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
            {user?.userType !== 'CLIENT' && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={isProvider() ? '/profil/szolgaltato' : '/profil'}
                  sx={{ textAlign: 'left' }}
                  onClick={handleDrawerToggle}
                >
                  <ListItemIcon><ManageAccounts /></ListItemIcon>
                  <ListItemText primary={isProvider() ? 'Szolgáltatói profil szerkesztése' : 'Profil szerkesztése'} />
                </ListItemButton>
              </ListItem>
            )}
            {isProvider() && user?.provider && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={`/szolgaltato/${user.provider.slug || user.provider.id}`}
                  sx={{ textAlign: 'left' }}
                  onClick={handleDrawerToggle}
                >
                  <ListItemIcon><Storefront /></ListItemIcon>
                  <ListItemText primary="Szolgáltatói profil megtekintése" />
                </ListItemButton>
              </ListItem>
            )}
            {isProvider() && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/galeria-kezeles"
                  sx={{ textAlign: 'left' }}
                  onClick={handleDrawerToggle}
                >
                  <ListItemIcon><PhotoLibrary /></ListItemIcon>
                  <ListItemText primary="Galéria kezelése" />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin() && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/admin" 
                  sx={{ textAlign: 'left' }}
                  onClick={handleDrawerToggle}
                >
                  <ListItemIcon><AdminPanelSettings /></ListItemIcon>
                  <ListItemText primary="Admin felület" />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton 
                component={Link} 
                to="/beallitasok" 
                sx={{ textAlign: 'left' }}
                onClick={handleDrawerToggle}
              >
                <ListItemIcon><Settings /></ListItemIcon>
                <ListItemText primary="Beállítások" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout} sx={{ textAlign: 'left' }}>
                <ListItemIcon><Logout /></ListItemIcon>
                <ListItemText primary="Kijelentkezés" />
              </ListItemButton>
            </ListItem>
            <Divider />
            {/* Ikonok a mobil menüben BADGE-ekkel */}
            {PAYMENT_ENABLED && user && (user.userType === 'CLIENT' || user.userType === 'ADMIN') && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/kosar"
                  sx={{ textAlign: 'left' }}
                  onClick={handleDrawerToggle}
                >
                  <ListItemIcon>
                    <Badge badgeContent={cartItems.length} color="error">
                      <ShoppingCart />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary="Kosár" />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/messages"
                sx={{ textAlign: 'left' }}
                onClick={handleDrawerToggle}
              >
                <ListItemIcon>
                  <MessageBadge>
                    <MessageIcon />
                  </MessageBadge>
                </ListItemIcon>
                <ListItemText primary="Üzenetek" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/ertesitesek"
                sx={{ textAlign: 'left' }}
                onClick={handleDrawerToggle}
              >
                <ListItemIcon>
                  <Badge
                    badgeContent={notificationCount}
                    color="error"
                    max={99}
                  >
                    <NotificationsIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText primary="Értesítések" />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, py: 1.5 }}>
        <Tooltip title="Instagram">
          <IconButton
            component="a"
            href="https://www.instagram.com/wedigo.hu/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            size="small"
            sx={{ color: 'text.secondary', '&:hover': { color: '#E1306C' } }}
          >
            <Instagram fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Facebook">
          <IconButton
            component="a"
            href="https://www.facebook.com/profile.php?id=61589355263973"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            size="small"
            sx={{ color: 'text.secondary', '&:hover': { color: '#1877F2' } }}
          >
            <Facebook fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <AppBar
      ref={headerRef}
      position={isHeaderTransparent ? 'absolute' : 'fixed'}
      elevation={appBarElevation}
      sx={{
        backgroundColor: isHeaderTransparent ? 'transparent' : '#1b331f',
        transition: 'background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out, border-bottom 0.3s ease-in-out',
        zIndex: (theme) => mobileOpen ? theme.zIndex.drawer - 1 : theme.zIndex.drawer + 1,
        borderBottom: isHeaderTransparent ? 'none' : `1px solid ${theme.palette.secondary.main}`,
      }}
    >
      <Toolbar>
        <Box
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <img
            src="/logo_name.png"
            alt="Wedigo Logo"
            style={{ height: '80px', marginRight: '8px' }}
          />
        </Box>

        {/* Hamburger menü ikon - X-re vált amikor nyitva van */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="end"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { md: 'none' }, color: 'white' }}
        >
          {mobileOpen ? (
            <CloseIcon />
          ) : (
            <Badge
              badgeContent={isAuthenticated ? totalUnreadCount : 0}
              color="error"
              max={99}
            >
              <MenuIcon />
            </Badge>
          )}
        </IconButton>

        {/* Desktop elemek */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
          {getNavItemsForUser().map((item) => (
            <Button
              key={item.name}
              color="inherit"
              component={Link}
              to={item.path}
              sx={{
                color: 'white',
                fontWeight: location.pathname.startsWith(item.path) ? 'bold' : 'normal',
                borderBottom: location.pathname.startsWith(item.path) ? '2px solid white' : 'none',
              }}
            >
              {item.name}
            </Button>
          ))}

          {!isAuthenticated ? (
            <>
              <Button
                color="inherit"
                component={Link}
                to="/login"
                sx={{ color: 'white' }}
              >
                Bejelentkezés
              </Button>
              <Button
                color="inherit"
                component={Link}
                to="/register"
                variant="outlined"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Regisztráció
              </Button>
            </>
          ) : (
            <>
              {PAYMENT_ENABLED && user && (user.userType === 'CLIENT' || user.userType === 'ADMIN') && (
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/kosar')}
                  title="Kosár"
                  sx={{ color: 'white' }}
                >
                  <Badge badgeContent={cartItems.length} color="error">
                    <ShoppingCart />
                  </Badge>
                </IconButton>
              )}

              <IconButton
                color="inherit"
                onClick={() => navigate('/messages')}
                title="Üzenetek"
                sx={{ color: 'white' }}
              >
                <MessageBadge>
                  <MessageIcon />
                </MessageBadge>
              </IconButton>

              <NotificationDropdown />

              <Chip
                icon={getUserTypeIcon()}
                label={getUserTypeLabel()}
                size="small"
                variant="outlined"
                color={getUserTypeColor()}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0,
                  overflow: 'hidden',
                  '&:hover': {
                    '& .user-name-typography': {
                      opacity: 1,
                      maxWidth: '200px',
                      mr: 1,
                                    },
                  },
                }}
              >
                <Typography
                  className="user-name-typography"
                  variant="body2"
                  sx={{
                    opacity: 0,
                    maxWidth: 0,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    transition: 'opacity 0.3s ease-in-out, max-width 0.3s ease-in-out, margin-right 0.3s ease-in-out',
                    color: 'white',
                    mr: 0,
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {getUserDisplayName()}
                </Typography>
                <IconButton
                  size="large"
                  aria-label="felhasználói menü"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                  sx={{ color: 'white' }}
                >
                  {user?.avatarUrl ? (
                    <Avatar
                      src={user.avatarUrl}
                      sx={{ width: 32, height: 32 }}
                    >
                      {user?.firstName?.charAt(0)}
                    </Avatar>
                  ) : (
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                      {user?.firstName?.charAt(0) || <AccountCircle />}
                    </Avatar>
                  )}
                </IconButton>
              </Box>

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={() => {
                    navigate('/dashboard');
                    handleClose();
                  }}
                >
                  <ListItemIcon><Dashboard /></ListItemIcon>
                  Dashboard
                </MenuItem>

                {user?.userType !== 'CLIENT' && (
                  <MenuItem
                    onClick={() => {
                      navigate(isProvider() ? '/profil/szolgaltato' : '/profil');
                      handleClose();
                    }}
                  >
                    <ListItemIcon><ManageAccounts /></ListItemIcon>
                    {isProvider() ? 'Profil szerkesztése' : 'Profil szerkesztése'}
                  </MenuItem>
                )}

                {isProvider() && (
                  <MenuItem
                    onClick={() => {
                      navigate(`/szolgaltato/${user.provider.slug || user.provider.id}`);
                      handleClose();
                    }}
                  >
                    <ListItemIcon><Storefront /></ListItemIcon>
                    Szolgáltatói profil
                  </MenuItem>
                )}
                {isAdmin() && (
                  <MenuItem
                    onClick={() => {
                      navigate('/admin');
                      handleClose();
                    }}
                  >
                    <ListItemIcon><AdminPanelSettings /></ListItemIcon>
                    Admin felület
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    navigate('/beallitasok');
                    handleClose();
                  }}
                >
                  <ListItemIcon><Settings /></ListItemIcon>
                  Beállítások
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><Logout /></ListItemIcon>
                  Kijelentkezés
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
      
      {/* Mobil menü Drawer (backdrop-pal) */}
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          anchor="right"
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 'auto',
              minWidth: 250,
              maxWidth: '80vw',
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </AppBar>
  );
};

export default Header;